import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { DfDocumentMeta, IrisDocument } from '@iris/common/models/IrisDocument';
import { omitBy } from 'lodash';

export const EDIT_PARAM_KEY = 'edit';

export interface PathToDocument {
  path: string[];
  queryParams: Params;
}

@Injectable({ providedIn: 'root' })
export class IrisDynamicFormsRoutingService {
  private readonly globalSandbox = inject(IrisGlobalSandbox);

  public getBaseCommands(includeConfiguration = false): string[] {
    const commands: string[] = [`/${this.globalSandbox.appAlias()}`];

    if (includeConfiguration) {
      commands.push('configuration');
    }

    commands.push('dynamic-forms');

    return commands;
  }

  public urlToDocumentList(pageCode: string, documentTemplateId: number, projectId: number): string {
    return this.pathToDocumentList(pageCode, documentTemplateId, projectId)?.path.join('/');
  }

  public pathToDocumentList(pageCode: string, documentTemplateId: number, projectId: number, filter?: Record<string, unknown>): PathToDocument {
    if (!pageCode || !documentTemplateId) { return null; }
    const compactFilter = filter ? omitBy(filter, t => t == null || (Array.isArray(t) && !t.length)) : null;
    return {
      path: [...this.getBaseCommands(), pageCode, 'documents', documentTemplateId.toString(), ...(projectId ? ['projects', projectId.toString()] : [])],
      queryParams: compactFilter && Object.keys(compactFilter).length ? {
        filter: JSON.stringify(compactFilter),
      } : {},
    };
  }

  public generateDocumentPathCode(meta: Partial<DfDocumentMeta>): string {
    return `${meta?.documentTemplateCode}${meta?.projectId ?? ''}-${meta?.number}`;
  }

  public pathToDocumentView(document: IrisDocument, backUrl?: string, pageCode?: string): PathToDocument {
    const requiredMeta: (keyof DfDocumentMeta)[]= document.meta.projectId ? ['documentTemplateCode', 'projectId', 'number'] : ['documentTemplateCode', 'number'];
    if (!requiredMeta.reduce((res, key) => res && document?.meta?.[key], true)) { return null; }
    return {
      path: [...this.getBaseCommands(), this.generateDocumentPathCode(document.meta)],
      queryParams: {
        backUrl: backUrl?.length ? backUrl : null,
        pageCode: pageCode?.length ? pageCode : null,
      },
    };
  }

  public pathToDocumentEdit(document: IrisDocument, backUrl?: string, pageCode?: string): PathToDocument {
    const viewTarget = this.pathToDocumentView(document, backUrl, pageCode);
    if (viewTarget == null) { return null; }
    return {
      ...viewTarget,
      queryParams: {
        ...viewTarget.queryParams,
        [EDIT_PARAM_KEY]: 1,
      },
    };
  }

  public urlToDocumentEdit(meta: Partial<DfDocumentMeta>): string {
    return [...this.getBaseCommands(), this.generateDocumentPathCode(meta)].join('/');
  }

  public urlToFormTemplates(documentTemplateId: number): string {
    return [
      ...this.getBaseCommands(true),
      'forms',
      documentTemplateId,
      'settings',
    ].join('/');
  }

  public urlToFormTemplateStructure(documentTemplateId: number): string {
    return [
      ...this.getBaseCommands(true),
      'forms',
      documentTemplateId,
      'structure',
    ].join('/');
  }

  public urlToDocumentPrintPreview(projectId: number, documentTemplateId: number, documentId: string, printConfigId: number, isAngular: boolean, printTemplateId?: number): string {
    const commands = [];

    if(!isAngular) {
      commands.push(...['ui', 'ui', 'dynamic-forms']);
    } else {
      commands.push(...this.getBaseCommands());
    }

    commands.push(...[
      'projects',
      projectId.toString(),
      'documents',
      documentTemplateId.toString(),
      'print',
      documentId,
      'config',
      printConfigId.toString(),
    ]);

    const url = commands.join('/');

    return printTemplateId ?
      `${url}?print=true&print-template=${printTemplateId}` :
      `${url}?print=true`;
  }

  urlToCombinedConfig(pageCode: string, combinedConfigId: number): string {
    return [
      ...this.getBaseCommands(true),
      'pages',
      pageCode,
      'configurations',
      'combined',
      combinedConfigId,
    ].join('/');
  }
}
